import React, { Suspense, useCallback, useEffect, useState } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import api from '../../services/api';
import { ManageableSession, SessionReportResponse } from './types';
// Tailwind utilities for the ported report. Every rule is emitted under
// `.report-tw` (see tailwind.config.js), so loading it app-wide cannot affect
// the MUI pages.
import './report/report-tailwind.generated.css';

// Lazily loaded: the report pulls in recharts, react-syntax-highlighter and
// lucide-react, which together roughly double the main bundle. It is a modal
// opened on demand, so every other page should not pay for it up front.
const ViewResult = React.lazy(() => import('./report/ViewResult'));

interface SessionReportDialogProps {
    open: boolean;
    session: ManageableSession | null;
    onClose: () => void;
}

/**
 * Thin wrapper around the recruiter app's ViewResult component.
 *
 * ViewResult is its own modal — it renders a fixed overlay and owns its
 * loading, error and close affordances — so this component only fetches the
 * payload and hands it over, deliberately without an MUI Dialog around it.
 * That is what keeps the Super Admin view identical to the recruiter view.
 */
const SessionReportDialog: React.FC<SessionReportDialogProps> = ({
    open,
    session,
    onClose,
}) => {
    const [report, setReport] = useState<SessionReportResponse | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');

    const sessionId = session?.id ?? null;

    const fetchReport = useCallback(async () => {
        if (!sessionId) return;
        setLoading(true);
        setError('');
        setReport(null);
        try {
            const response = await api.get(
                `/super-admin/interview-sessions/${sessionId}/report/`,
            );
            setReport(response.data);
        } catch (err: any) {
            console.error('Error fetching session report:', err);
            // A 404 is the ordinary "feedback was never generated" case, which
            // the recovery actions in SessionActionsDialog exist to fix.
            setError(
                err.response?.status === 404
                    ? 'No report has been generated for this interview yet. '
                      + 'Use the recovery actions to regenerate feedback.'
                    : err.response?.data?.error
                      || err.message
                      || 'Failed to load the assessment report',
            );
        } finally {
            setLoading(false);
        }
    }, [sessionId]);

    useEffect(() => {
        if (!open || !sessionId) {
            setReport(null);
            setError('');
            return;
        }
        fetchReport();
    }, [open, sessionId, fetchReport]);

    if (!open || !session) return null;

    return (
        <div className="report-tw">
            <Suspense
                fallback={(
                    <Backdrop open sx={{ zIndex: 999999, color: '#fff' }}>
                        <CircularProgress color="inherit" />
                    </Backdrop>
                )}
            >
                <ViewResult
                    feedback={report?.feedback ?? null}
                    sessionData={report?.session ?? null}
                    loadingFeedback={loading}
                    feedbackError={error}
                    onClose={onClose}
                />
            </Suspense>
        </div>
    );
};

export default SessionReportDialog;
